"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import { toAvatarUrl } from "@/lib/media";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { getJson } from "@/lib/api";
import VerifiedBadge from "@/components/common/VerifiedBadge";
import { scrubContactInfo } from "@/lib/scrubContact";
import { useSession } from "next-auth/react";

export type MiniTalentProps = {
  id?: string | number | null;
  name?: string | null;
  avatarUrl?: string | null;
  profileUrl?: string | null;
  slug?: string | null;
  subtitle?: React.ReactNode;
  className?: string;
  // Optional explicit meta; if omitted and id present, component will fetch
  age?: number | string | null;
  sex?: string | null;
  location?: string | null;
  isVerified?: boolean;
};

export default function MiniTalentCard({ id, name, avatarUrl, profileUrl, slug, subtitle, className, age, sex, location, isVerified = false }: MiniTalentProps) {
  const resolvedAvatar = toAvatarUrl(avatarUrl || undefined);
  const href = (profileUrl && String(profileUrl).trim())
    ? String(profileUrl)
    : (slug ? `/u/${encodeURIComponent(slug)}` : null)
    ?? (id != null ? `/details/${encodeURIComponent(String(id))}` : undefined);

  // Derived meta: age/sex/location. If not provided, attempt lightweight fetch by id
  const [meta, setMeta] = React.useState<{ age?: number | null; sex?: string | null; location?: string | null }>(() => {
    // Seed from props if available
    const initial: any = {};
    if (typeof age === 'number') initial.age = age;
    else if (typeof age === 'string') {
      const n = Number(age); if (!isNaN(n) && n > 0 && n < 120) initial.age = Math.floor(n);
    }
    if (sex) initial.sex = sex;
    if (location) initial.location = location;
    return initial;
  });

  React.useEffect(() => {
    // Only fetch if we don't have meta and have an id
    if (!id) return;
    if (meta.age != null && meta.sex && meta.location) return;
    let cancelled = false;
    (async () => {
      try {
        const pid = encodeURIComponent(String(id));
        let j: any = null;
        try {
          j = await getJson(`/api/people/${pid}`);
        } catch {
          try { j = await getJson(`/api/person/${pid}/detail`); if (j && j.person) j = j.person; } catch {}
        }
        if (!j || cancelled) return;
        // Age: prefer numeric age; fallback to DOB
        let a: number | null = null;
        const rawAge = j.age ?? j.Age ?? null;
        if (typeof rawAge === 'number' && rawAge > 0 && rawAge < 120) a = Math.floor(rawAge);
        else {
          const dob = j.dateOfBirth ?? j.DateOfBirth ?? j.dob ?? null;
          if (dob) {
            try {
              const d = new Date(String(dob));
              if (!isNaN(d.getTime())) {
                const now = new Date();
                let an = now.getFullYear() - d.getFullYear();
                const m = now.getMonth() - d.getMonth();
                if (m < 0 || (m === 0 && now.getDate() < d.getDate())) an--;
                if (an > 0 && an < 120) a = an;
              }
            } catch {}
          }
        }
        // Sex/gender
        let sx: string | null = null;
        const rawSex = j.sex ?? j.Sex ?? j.gender ?? j.Gender ?? null;
        if (rawSex) sx = String(rawSex);
        // Location
        let loc: string | null = null;
        const locName = j.locationName ?? j.LocationName ?? j.city ?? j.City ?? null;
        const state = j.state ?? j.State ?? j.stateCode ?? j.StateCode ?? null;
        if (locName && state) loc = `${String(locName).split(',')[0].trim()}, ${String(state).trim()}`;
        else if (locName) loc = String(locName).split(',')[0].trim();
        if (!cancelled) setMeta(prev => ({ age: prev.age ?? a ?? null, sex: prev.sex ?? sx, location: prev.location ?? loc }));
      } catch {}
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const metaLine = React.useMemo(() => {
    const niceSex = meta.sex ? (meta.sex.charAt(0).toUpperCase() + meta.sex.slice(1)) : undefined;
    const pieces = [
      meta.age != null ? String(meta.age) : undefined,
      niceSex,
      meta.location ? scrubContactInfo(meta.location) : undefined,
    ].filter(Boolean) as string[];
    if (pieces.length) {
      return <span className="block truncate text-xs text-muted-foreground">{pieces.join(' \u00B7 ')}</span>;
    }
    return subtitle ? (<span className="block truncate text-xs text-muted-foreground">{subtitle}</span>) : null;
  }, [subtitle, meta.age, meta.sex, meta.location]);

  const inner = (
    <span className="flex items-center gap-3 w-full">
      <span className="relative h-8 w-8 rounded-full overflow-hidden ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-white/10 shadow-sm flex-shrink-0">
        <Image src={resolvedAvatar} alt={name ? `${name} avatar` : 'Talent avatar'} fill sizes="32px" className="object-cover" />
      </span>
      <span className="min-w-0 leading-tight flex-1">
        {name ? (
          <span className={cn(
            "flex items-center gap-1.5 text-sm font-medium text-foreground",
            href ? "group-hover:underline underline-offset-2" : "",
          )}>
            <span className="truncate">{scrubContactInfo(name)}</span>
            {isVerified && <VerifiedBadge size={14} />}
          </span>
        ) : null}
        {metaLine}
      </span>
      {href ? (
        <span className="ml-3 flex items-center text-foreground/40 group-hover:text-foreground/60 transition-colors">
          <ChevronRight aria-hidden className="h-4 w-4" />
        </span>
      ) : null}
    </span>
  );

  return href ? (
    <Link
      href={href}
      className={cn(
        "group inline-flex items-center gap-3 w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2 rounded-md transition-opacity hover:opacity-95",
        className,
      )}
      aria-label={name ? `View ${name}` : undefined}
      title={name ? `View ${name}` : undefined}
    >
      {inner}
    </Link>
  ) : (
    <span className={cn("inline-flex items-center gap-3", className)}>
      {inner}
    </span>
  );
}
